import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { AppState } from '../../store/state/app.state';
import { FilterModel } from '../../warehouse/models/filter.model';
import { ResponseList, ResponseModel, FormInputChangedModel, DocumentTemplateModel, PositionGroupModel } from '../../shared/models';
import { SubscriptionListModel, SubscriptionModel } from '../models/subscription.model';
import { AffectSubscriptionEnum, SubscriptionListTabsEnum, SubscriptionPeriodEnum } from '../enums';
import {
  DecrementLoadingRequestsCount,
  IncrementLoadingRequestsCount,
  LoadSubscription,
  LoadSubscriptionPositions,
  LoadSubscriptionPositionsGroups,
  LoadSubscriptionsList,
  UpdateShouldRefreshEntity,
  UpdateSubscription,
  UpdateSubscriptionCurrentState,
  UpdateSubscriptionUpdatedAt
} from '../store/actions/subscription.actions';
import { SalesPosition, SalesPositionsByProductType } from '../models/subscription-position.model';
import { FileUploadParams } from 'common/src/models/file-upload-params.model';
import { UIStatesEnum } from 'common/src/models';
import { DisplayToaster } from '../../shared/decorators/toaster';
import { ToasterService } from 'common/src/modules/ui-components/toaster';
import { ResponseSubscriptionCountersModel, SubscriptionCountersModel } from '../models/subscription-counters.model';
import { CommonModalsActionsEnum, InfoModalComponent, WarningModalComponent } from 'common/src/modules/modals/modals-common';
import { getAnotherUserEditErrorModalData } from 'common/src/modules/modals/modals-common/common-modal.config';
import { PurchaseOrder } from '../../purchase-order/models';
import { DEFAULT_SORT_DIRECTION } from '../../shared/constants';
import { TradePositionModel } from '../../trade/models';
import { PositionMediaTypeEnum } from '../../shared/enums';
import { PdfSettingsAlignEnum } from '../../pdf-settings/enums';

@Injectable()
export class SubscriptionApiService {
  private readonly apiEndpoint: string = '/subscriptions';

  private apiUrl(url: string = ''): string {
    return this.apiEndpoint + url;
  }

  constructor(
    private readonly toasterService: ToasterService,
    private readonly http: HttpClient,
    private readonly translateService: TranslateService,
    private readonly dialog: MatDialog,
    private readonly store: Store<AppState>,
  ) {}

  createSubscriptionDraft(): Observable<SubscriptionModel> {
    return this.http
      .post<ResponseModel<SubscriptionModel>>(this.apiUrl('/draft'), {})
      .pipe(
        map((data: ResponseModel<SubscriptionModel>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error);
          return throwError(error);
        })
      );
  }

  getSubscriptionList(
    status: SubscriptionListTabsEnum,
    page: string = '1',
    per_page: string = '100',
    sort: FilterModel = { nameColumn: 'updated_at', value: DEFAULT_SORT_DIRECTION },
    filters: any = {}
  ): Observable<ResponseList<SubscriptionListModel>> {
    const params = {
      page,
      per_page,
      [`sort[${sort.nameColumn}]`]: sort.value
    };

    if (status === SubscriptionListTabsEnum.ALL) {
      params['filters[status]'] = [
        SubscriptionListTabsEnum.DRAFT,
        SubscriptionListTabsEnum.OPEN,
        SubscriptionListTabsEnum.PAUSED,
        SubscriptionListTabsEnum.COMPLETED,
        SubscriptionListTabsEnum.CANCELED,
        SubscriptionListTabsEnum.DELETED,
      ].join(',');
      params['filters[allStatus]'] = 'true';
    } else {
      params['filters[status]'] = status;
    }

    for (const [key, value] of Object.entries(filters)) {
      params[`filters[${key}]`] = Array.isArray(value) ? value.join(',') : value.toString();
    }
    return this.http.get<ResponseList<SubscriptionListModel>>(this.apiUrl(), { params })
      .pipe(tap((data: ResponseList<SubscriptionListModel>) => {
        this.store.dispatch(LoadSubscriptionsList({
          subscriptionsListData: {
            [data.pagination.page]: {
              pagination: data.pagination,
              sort,
              search: filters.search,
              data: data.data
            }
          },
          status,
          page: data.pagination.page
        }));
      }));
  }

  getSubscriptionListPositions(subscriptionId: number): Observable<TradePositionModel[]> {
    return this.http.get<ResponseModel<TradePositionModel[]>>(this.apiUrl(`/${subscriptionId}/list/positions`))
      .pipe(map((data: ResponseModel<TradePositionModel[]>) => data.data));
  }

  getSubscriptionsRunpleIds(status: SubscriptionListTabsEnum): Observable<{ id: number; runpleId: string }[]> {
    const params = { };

    if (status === SubscriptionListTabsEnum.ALL) {
      params['status'] = [
        SubscriptionListTabsEnum.DRAFT,
        SubscriptionListTabsEnum.OPEN,
        SubscriptionListTabsEnum.PAUSED,
        SubscriptionListTabsEnum.COMPLETED,
        SubscriptionListTabsEnum.CANCELED,
        SubscriptionListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<ResponseModel<{ id: number; runpleId: string }[]>>(this.apiUrl(`/runpleIds`), { params })
      .pipe(map((data: ResponseModel<{ id: number; runpleId: string }[]>) => data.data.filter(itm => !!itm.runpleId)));
  }

  getSubscriptionsFiltersBranchOffice(status: SubscriptionListTabsEnum): Observable<{ id: number; name: string }[]> {
    const params = { };

    if (status === SubscriptionListTabsEnum.ALL) {
      params['status'] = [
        SubscriptionListTabsEnum.DRAFT,
        SubscriptionListTabsEnum.OPEN,
        SubscriptionListTabsEnum.PAUSED,
        SubscriptionListTabsEnum.COMPLETED,
        SubscriptionListTabsEnum.CANCELED,
        SubscriptionListTabsEnum.DELETED,
      ].join(',');
    } else {
      params['status'] = status;
    }

    return this.http.get<ResponseModel<{ id: number; name: string }[]>>(this.apiUrl(`/filters/branch-office`), { params })
      .pipe(map((data: ResponseModel<{ id: number; name: string }[]>) => data.data));
  }

  getSubscriptionCounters(): Observable<SubscriptionCountersModel> {
    return this.http
      .get<ResponseModel<ResponseSubscriptionCountersModel>>(
        this.apiUrl('/counters')
      )
      .pipe(
        map(
          (data: ResponseModel<ResponseSubscriptionCountersModel>) =>
            data.data.subscriptions
        )
      );
  }

  public getSBCListExport(status: string): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: this.apiUrl(`/${status}/csv`),
      type: 'zip',
    };
    return of(fileParams);
  }

  public getSBCListExportAll(): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: this.apiUrl('/csv'),
      type: 'zip',
    };
    return of(fileParams);
  }

  @DisplayToaster({showErrorMessage: true})
  changeSubscriptionStatusBatch(status: SubscriptionListTabsEnum,ids: number[]): Observable<any> {
    const body = { status, ids };
    return this.http.patch(this.apiUrl('/status'), body);
  }

  @DisplayToaster({showErrorMessage: true})
  changeSubscriptionsStatus(formData: any): Observable<any> {
    return this.http.patch(this.apiUrl('/status'), formData);
  }

  @DisplayToaster({showErrorMessage: true})
  getSubscriptionPauseBillingDate(
    subscriptionId: number,
    pauseData: {
      status: SubscriptionListTabsEnum,
      startDate: Date,
      endDate: Date,
      duration: SubscriptionPeriodEnum,
      affectOnSubscription: AffectSubscriptionEnum,
    }
  ): Observable<any> {
    return this.http.post(this.apiUrl(`/${subscriptionId}/pause/billing-date`), pauseData)
      .pipe(
        map((data: ResponseModel<any>) => data.data),
      );
  }

  changeSubscriptionStatus(status: SubscriptionListTabsEnum, subscriptionId: number, formData?: any): Observable<SubscriptionModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    const body = { status, ...formData };
    return this.http
      .patch(this.apiUrl(`/${subscriptionId}/status`), body)
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateShouldRefreshEntity({ isShouldRefresh: false }));
        }),
        map((data: ResponseModel<SubscriptionModel>) => data.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  getSubscriptionById(id: string | number): Observable<SubscriptionModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http
      .get<ResponseModel<SubscriptionModel>>(this.apiUrl(`/${id}`))
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount()))
      );
  }

  updateSubscription(subscriptionId: number, field: FormInputChangedModel, force = false): Observable<SubscriptionModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<SubscriptionModel>>(this.apiUrl(`/${subscriptionId}`), {...field, force})
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, subscriptionId, field);
          return throwError(error);
        })
      );
  }

  updateBillingInfoAddressByCustomer(subscriptionId: number): Observable<SubscriptionModel> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.request<ResponseModel<SubscriptionModel>>('put', this.apiUrl(`/${subscriptionId}/billing-address/update-by-customer`))
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, subscriptionId);
          return throwError(error);
        })
      );
  }

  @DisplayToaster({showErrorMessage: true})
  createSubscription(id: number): Observable<SubscriptionModel> {
    return this.http.request<ResponseModel<SubscriptionModel>>('post', this.apiUrl(`/${id}/accept-create`))
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
        }),
        map((data: ResponseModel<SubscriptionModel>) => data.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  createSubscriptionsInvoice(subscriptionId: number, invoiceId: number): Observable<SubscriptionModel> {
    return this.http.post<ResponseModel<SubscriptionModel>>(this.apiUrl(`/${subscriptionId}/invoice`), {id: invoiceId})
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
        }),
        map((data: ResponseModel<SubscriptionModel>) => data.data)
      );
  }

  getSubscriptionPositions(subscriptionId: number): Observable<ResponseModel<SalesPositionsByProductType>> {
    return this.http.get<ResponseModel<SalesPositionsByProductType>>(this.apiUrl(`/${subscriptionId}/positions`))
      .pipe(
        tap((response: ResponseModel<SalesPositionsByProductType>) => {
          this.store.dispatch(LoadSubscriptionPositions({positions: response}));
        }),
      );
  }

  createSubscriptionPosition(subscriptionId: number, position: SalesPosition): Observable<ResponseModel<SalesPositionsByProductType>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.post<ResponseModel<SalesPositionsByProductType>>(this.apiUrl(`/${subscriptionId}/positions`), position)
      .pipe(
        tap((response: ResponseModel<SalesPositionsByProductType>) => {
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
          this.store.dispatch(LoadSubscriptionPositions({positions: response}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, subscriptionId);
          return throwError(error);
        })
      );
  }

  updateSubscriptionPosition(positionId: number, field: FormInputChangedModel, subscriptionId): Observable<ResponseModel<SalesPosition>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    return this.http.patch<ResponseModel<SalesPosition>>(this.apiUrl(`/positions/${positionId}`), field).pipe(
      tap(() => {
        this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
      }),
      finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
      catchError(error => {
        this.handlePopupErrors(error, subscriptionId);
        return throwError(error);
      })
    );
  }

  deleteSubscriptionPositions(subscriptionId: number, ids: number[]): Observable<ResponseModel<SalesPositionsByProductType>> {
    this.store.dispatch(IncrementLoadingRequestsCount());

    const body = { ids: ids };
    return this.http.request<ResponseModel<SalesPositionsByProductType>>(
      'delete',
      this.apiUrl(`/${subscriptionId}/positions`),
      {body}
    )
      .pipe(
        tap((response: ResponseModel<SalesPositionsByProductType>) => {
          this.store.dispatch(LoadSubscriptionPositions({positions: response}));
          this.store.dispatch(UpdateSubscriptionUpdatedAt({updatedAt: new Date()}));
        }),
        finalize(() => this.store.dispatch(DecrementLoadingRequestsCount())),
        catchError(error => {
          this.handlePopupErrors(error, subscriptionId);
          return throwError(error);
        })
      );
  }

  changePositionOrder(posId: number, moveToOrder: number): any {
    const body = {
      orderPosition: moveToOrder,
      id: posId,
    }
    return this.http.patch(this.apiUrl(`/positions/${posId}/order/${moveToOrder}`), body)
      .pipe(
        map((data: ResponseModel<PurchaseOrder>) => data.data),
        catchError(error => {
          this.showMsg('error', error.error.message);
          return throwError(error);
        })
      );
  }

  @DisplayToaster({showErrorMessage: true})
  getSubscriptionGroupedPositions(subscriptionId: number): Observable<ResponseModel<PositionGroupModel[]>> {
    return this.http.get<ResponseModel<PositionGroupModel[]>>(this.apiUrl(`/${subscriptionId}/grouped/positions`))
      .pipe(
        tap((response: ResponseModel<PositionGroupModel[]>) => {
          this.store.dispatch(LoadSubscriptionPositionsGroups({positionsGroups: response.data}));
        }),
      );
  }

  @DisplayToaster({showErrorMessage: true})
  moveSubscriptionGroupedPositions(subscriptionId: number, body: {groupId: number|string; ids: number[]}): Observable<ResponseModel<PositionGroupModel[]>> {
    return this.http.patch<ResponseModel<PositionGroupModel[]>>(this.apiUrl(`/${subscriptionId}/positions/group`), body)
      .pipe(
        tap((response: ResponseModel<PositionGroupModel[]>) => {
          this.store.dispatch(LoadSubscriptionPositionsGroups({positionsGroups: response.data}));
        }),
      );
  }

  public createPositionMedia(
    sbcId: number,
    mediaType: PositionMediaTypeEnum,
    data: {
      groupId?: number,
      file?: File,
      description?: string,
      width?: number,
      height?: number,
      alignment?: PdfSettingsAlignEnum,
      useAspectRatio?: boolean,
    }
  ): Observable<PositionGroupModel[]> {
    let params = new HttpParams().set('mediaType', mediaType);
    if (data.groupId) { params = params.set('groupId', data.groupId.toString()); }
    if (data.width) { params = params.set('width', data.width.toString()); }
    if (data.height) { params = params.set('height', data.height.toString()); }
    if (data.alignment) { params = params.set('alignment', data.alignment); }
    if (data.useAspectRatio) { params = params.set('useAspectRatio', data.useAspectRatio.toString()); }

    const formData = new FormData();
    if (data.file) { formData.set('file', data.file); }
    if (data.description) { formData.set('description', data.description); }

    return this.http.post<ResponseModel<PositionGroupModel[]>>(this.apiUrl(`/${sbcId}/positions/media`), formData, {params})
      .pipe(
        map((data: ResponseModel<PositionGroupModel[]>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error, sbcId);
          return throwError(error);
        })
      );
  }

  public deleteMediaPositionFile(sbcId: number, positionId: number): Observable<null> {
    return this.http.delete<ResponseModel<null>>(this.apiUrl(`/${sbcId}/positions/${positionId}/file`))
      .pipe(
        map((data: ResponseModel<null>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error, sbcId);
          return throwError(error);
        })
      );
  }

  public updateMediaPositionFile(sbcId: number, positionId: number, file: File, width?: number, height?: number): Observable<PositionGroupModel[]> {
    let params = new HttpParams();
    if (width) { params = params.set('width', width.toString()); }
    if (height) { params = params.set('height', height.toString()); }

    const formData = new FormData();
    if (file) { formData.set('file', file); }
    return this.http.post<ResponseModel<PositionGroupModel[]>>(this.apiUrl(`/${sbcId}/positions/${positionId}/file`), formData, {params})
      .pipe(
        map((data: ResponseModel<PositionGroupModel[]>) => data.data),
        catchError(error => {
          this.handlePopupErrors(error, sbcId);
          return throwError(error);
        })
      );
  }

  @DisplayToaster({showErrorMessage: true})
  deleteSubscriptionsPermanently(ids: number[]): Observable<ResponseModel<null>> {
    const body = { ids };
    return this.http.request<ResponseModel<null>>('delete', this.apiUrl(), {body});
  }

  @DisplayToaster({showErrorMessage: true})
  deleteSubscriptionPermanently(subscriptionId: number): Observable<ResponseModel<null>> {
    return this.http.request<ResponseModel<null>>('delete', this.apiUrl(`/${subscriptionId}`));
  }

  public getSubscriptionExportParams(id: number, title: string): Observable<FileUploadParams> {
    const fileParams: FileUploadParams = {
      url: `accounting/v1/pdf/subscription/${id}`,
      type: 'pdf',
      title,
    };
    return of(fileParams);
  }

  public cloneSubscription(subscriptionId: number): Observable<SubscriptionModel> {
    return this.http.post<ResponseModel<SubscriptionModel>>(this.apiUrl(`/${subscriptionId}/clone`), {})
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(LoadSubscription({ subscription: response.data }));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data)
      );
  }

  @DisplayToaster({showErrorMessage: true})
  public sendSubscriptionEmail(subscriptionId: number): Observable<any> {
    return this.http.post(this.apiUrl(`/${subscriptionId}/email`), {});
  }

  public subscriptionSetEdit(id: string | number, force = false): Observable<SubscriptionModel> {
    const body = { force };

    return this.http.put<ResponseModel<SubscriptionModel>>( this.apiUrl(`/${id}/locking`), body)
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(UpdateSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateSubscriptionCurrentState({ currentState: UIStatesEnum.EDIT }));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, id);
          return throwError(error);
        })
      );
  }

  public subscriptionUnsetEdit(id: string | number): Observable<SubscriptionModel> {
    return this.http.request<ResponseModel<SubscriptionModel>>('put', this.apiUrl(`/${id}/unlocking`))
      .pipe(
        tap((response: ResponseModel<SubscriptionModel>) => {
          this.store.dispatch(UpdateSubscription({ subscription: response.data }));
          this.store.dispatch(UpdateSubscriptionCurrentState({ currentState: UIStatesEnum.VIEW }));
        }),
        map((response: ResponseModel<SubscriptionModel>) => response.data),
        catchError(error => {
          this.handlePopupErrors(error, id);
          return throwError(error);
        })
      );
  }

  public getDocumentTemplates(): Observable<DocumentTemplateModel[]> {
    return this.http.get<ResponseModel<DocumentTemplateModel[]>>(this.apiUrl('/templates'))
      .pipe(map((response: ResponseModel<DocumentTemplateModel[]>) => response.data));
  }

  public getDocumentTemplatesNames(): Observable<{ id: number; name: string; }[]> {
    return this.http.get<ResponseModel<{ id: number; name: string; }[]>>(this.apiUrl('/templates/names'))
      .pipe(map((response: ResponseModel<{ id: number; name: string; }[]>) => response.data));
  }

  public saveDocumentTemplate(documentId: number, body: { name: string; overwrite: boolean; resetCounter: boolean; }): Observable<boolean> {
    return this.http.post<ResponseModel<boolean>>(this.apiUrl(`/${documentId}/template`), body)
      .pipe(map((response: ResponseModel<boolean>) => response.data));
  }

  public applyDocumentTemplate(documentId: number, templateId: number, force = false): Observable<any> {
    return this.http.patch<ResponseModel<any>>(this.apiUrl(`/${documentId}/template/${templateId}`), {force})
      .pipe(map((response: ResponseModel<any>) => response.data));
  }

  @DisplayToaster({showErrorMessage: true})
  public createDocumentFromTemplate(templateId: number): Observable<number> {
    return this.http.request<ResponseModel<number>>('post', this.apiUrl(`/template/${templateId}`))
      .pipe(map((response: ResponseModel<number>) => response.data));
  }

  public showMsg(type: string, message: string): void {
    this.toasterService.notify({ type, message });
  }

  private handlePopupErrors(error: HttpErrorResponse, subscriptionId?: string | number, field?: FormInputChangedModel): void {
    if (!!error.error.message) {
      switch (error.error.message) {
        case 'anotherUserEditError':
        {
          const dialog = this.dialog.open(WarningModalComponent, {
            data: getAnotherUserEditErrorModalData(
              {
                document: error.error.data.entityName,
                userName: error.error.data.userName,
              },
              this.translateService
            ),
            backdropClass: 'backdrop-none',
          });

          dialog.afterClosed().subscribe(res => {
            if (res === CommonModalsActionsEnum.CONFIRM) {
              this.subscriptionSetEdit(subscriptionId, true).subscribe();
            }
          });
        }
          break;
        case 'billingFrequencyUpdateError':
        {
          const dialog = this.dialog.open(WarningModalComponent, {
            data: {
              title: 'SUBSCRIPTIONS.CHANGE_BILLING_FREQUENCY',
              message: 'SUBSCRIPTIONS.CHANGE_BILLING_FREQUENCY_MSG',
              confirmBtnText: 'BUTTON.CHANGE',
              confirmBtnIcon: 'checkbox-on',
            }
          });

          dialog.afterClosed().subscribe(res => {
            if (res === CommonModalsActionsEnum.CONFIRM) {
              this.updateSubscription(+subscriptionId, field, true).subscribe();
            } else {
              this.getSubscriptionById(subscriptionId).subscribe();
            }
          });
        }
          break;
        case 'billingDateUpdateError':
        {
          const dialog = this.dialog.open(WarningModalComponent, {
            data: {
              title: 'SUBSCRIPTIONS.CHANGE_BILLING_DATE',
              message: 'SUBSCRIPTIONS.CHANGE_BILLING_DATE_MSG',
              confirmBtnText: 'BUTTON.CHANGE',
              confirmBtnIcon: 'checkbox-on',
            }
          });

          dialog.afterClosed().subscribe(res => {
            if (res === CommonModalsActionsEnum.CONFIRM) {
              this.updateSubscription(+subscriptionId, field, true).subscribe();
            } else {
              this.getSubscriptionById(subscriptionId).subscribe();
            }
          });
        }
          break;
        case 'billingFrequencyUpdateHardError':
        {
          const dialog = this.dialog.open(InfoModalComponent, {
            data: {
              title: 'SUBSCRIPTIONS.UNABLE_CHANGE_BILLING_FREQUENCY',
              message: 'SUBSCRIPTIONS.UNABLE_CHANGE_BILLING_FREQUENCY_MSG',
              hideCancelBtn: true,
              titleIcon: 'alert-triangle',
              titleColor: 'yellow-400',
              confirmBtnText: 'BUTTON.CLOSE',
              confirmBtnIcon: 'close'
            }
          });

          dialog.afterClosed().subscribe(() => {
            this.getSubscriptionById(subscriptionId).subscribe();
          });
        }
          break;
        case 'billingDateUpdateHardError':
        {
          const dialog = this.dialog.open(InfoModalComponent, {
            data: {
              title: 'SUBSCRIPTIONS.UNABLE_CHANGE_BILLING_DATE',
              message: 'SUBSCRIPTIONS.UNABLE_CHANGE_BILLING_DATE_MSG',
              hideCancelBtn: true,
              titleIcon: 'alert-triangle',
              titleColor: 'yellow-400',
              confirmBtnText: 'BUTTON.CLOSE',
              confirmBtnIcon: 'close'
            }
          });

          dialog.afterClosed().subscribe(() => {
            this.getSubscriptionById(subscriptionId).subscribe();
          });
        }
          break;
        case 'notEditModeError':
          const documentName = this.translateService.instant('APP.RECURRING_INVOICE');
          this.showMsg('warning', this.translateService.instant('COMMON.DOC_UPDATED_BY_USER', { document: documentName }));
          this.store.dispatch(UpdateSubscriptionCurrentState({ currentState: UIStatesEnum.VIEW }));
          this.getSubscriptionById(subscriptionId).subscribe();
          break;
        default:
          this.showMsg('error', error.error.message);
          break
      }
    }
  }

}
